import { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useSearchParams } from 'react-router-dom';
import Icon from '../components/Icon';
import { EmptyState, Page, PageHead, Photo, SkeletonGrid, Tabs } from '../components/UI';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';
import { cmToFeet, timeAgo } from '../utils/format';

const EMPTY = {
  received: ['inbox', 'No new interests', 'When someone likes your profile, they will appear here.'],
  sent: ['send', "You haven't sent interests yet", 'Find someone you like and tap “Send interest”.'],
  accepted: ['heart', 'No connections yet', 'Accepted interests turn into conversations.'],
  declined: ['x', 'Nothing here', 'Declined interests are kept here for reference.'],
};

function InterestRow({ item, onRespond, onWithdraw }) {
  const p = item.profile;
  return (
    <motion.article
      layout
      className="irow"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, x: item._exit === 'accepted' ? 120 : -120, transition: { duration: 0.3 } }}
    >
      <Link to={`/profile/${p.profileId}`} className="irow__photo"><Photo src={p.photo} alt={p.name} locked={p.photoLocked} /></Link>
      <div className="irow__body">
        <div className="irow__head">
          <Link to={`/profile/${p.profileId}`}><h3>{p.name} {p.verified && <Icon name="shield" size={15} className="verified" />}</h3></Link>
          <small className="muted">{timeAgo(item.updatedAt)}</small>
        </div>
        <p className="muted">{[p.age && `${p.age} yrs`, cmToFeet(p.heightCm), p.religion, p.city].filter(Boolean).join(' · ')}</p>
        <p className="irow__job"><Icon name="briefcase" size={14} /> {[p.specialty || p.profession, p.employer].filter(Boolean).join(' at ')}</p>
        {item.message && item.direction === 'received' && <p className="irow__msg">“{item.message}”</p>}
        <div className="irow__actions">
          {item.direction === 'received' && item.status === 'pending' && (
            <>
              <motion.button whileTap={{ scale: 0.94 }} className="btn btn--primary btn--sm" onClick={() => onRespond(item, 'accepted')}><Icon name="check" size={15} stroke={2.4} /> Accept</motion.button>
              <button className="btn btn--ghost btn--sm" onClick={() => onRespond(item, 'declined')}>Decline</button>
            </>
          )}
          {item.direction === 'sent' && item.status === 'pending' && (
            <>
              <span className="pill pill--soft"><Icon name="send" size={13} /> Awaiting reply</span>
              <button className="link-btn" onClick={() => onWithdraw(item)}>Withdraw</button>
            </>
          )}
          {item.status === 'accepted' && <Link to={`/messages/${p.profileId}`} className="btn btn--teal btn--sm"><Icon name="chat" size={15} /> Chat now</Link>}
          {item.status === 'declined' && <span className="pill pill--soft">{item.direction === 'sent' ? 'They declined' : 'You declined'}</span>}
        </div>
      </div>
    </motion.article>
  );
}

export default function Interests() {
  const { toast, refreshCounts, counts } = useApp();
  const [params, setParams] = useSearchParams();
  const box = params.get('box') || 'received';
  const [items, setItems] = useState(null);
  const [summary, setSummary] = useState({});

  const load = useCallback(() => {
    setItems(null);
    api(`/interests?box=${box}`).then((d) => setItems(d.items)).catch(() => setItems([]));
    api('/interests/summary').then(setSummary).catch(() => {});
  }, [box]);
  useEffect(load, [load]);

  const respond = async (item, status) => {
    try {
      const r = await api(`/interests/${item.id}`, { method: 'PUT', body: { status } });
      item._exit = status;
      setItems((list) => list.filter((x) => x.id !== item.id));
      toast(r.message, status === 'accepted' ? 'success' : 'default');
      refreshCounts();
    } catch (e) {
      toast(e.message, 'error');
    }
  };
  const withdraw = async (item) => {
    try {
      const r = await api(`/interests/${item.id}`, { method: 'DELETE' });
      setItems((list) => list.filter((x) => x.id !== item.id));
      toast(r.message);
    } catch (e) {
      toast(e.message, 'error');
    }
  };

  const [icon, title, text] = EMPTY[box];
  return (
    <Page>
      <div className="container narrow">
        <PageHead eyebrow="Inbox" title="Interests" sub="Every connection starts with a little hello." />
        <Tabs
          id="interests"
          value={box}
          onChange={(v) => setParams({ box: v })}
          tabs={[
            { value: 'received', label: 'Received', icon: 'inbox', count: counts.interests },
            { value: 'sent', label: 'Sent', icon: 'send', count: summary.sent },
            { value: 'accepted', label: 'Accepted', icon: 'heart', count: summary.accepted },
            { value: 'declined', label: 'Declined', icon: 'x' },
          ]}
        />
        <div className="ilist">
          {!items ? (
            <SkeletonGrid count={3} />
          ) : !items.length ? (
            <EmptyState icon={icon} title={title} text={text} action={<Link to="/matches" className="btn btn--primary">Explore matches</Link>} />
          ) : (
            <AnimatePresence mode="popLayout">
              {items.map((it) => <InterestRow key={it.id} item={it} onRespond={respond} onWithdraw={withdraw} />)}
            </AnimatePresence>
          )}
        </div>
      </div>
    </Page>
  );
}
